/* The four ways a workspace skill comes in (tasks/custom-skills/01-design-mock.md). A way
   whose backend hasn't landed shows switched off (`isSkillActionLive`); with none live,
   New skill itself is. */
import React from "react";
import { ChevronDown, Github, PenLine, Plus, Upload, WandSparkles } from "lucide-react";

import { isSkillActionLive, type SkillAction } from "@/shared/api/skills";
import { BrandButton } from "@/shared/components/ui-kit";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";

import type { LucideIcon } from "lucide-react";

export type NewSkillWay = "upload" | "write" | "ask" | "github";

const WAYS: { key: NewSkillWay; action: SkillAction; Icon: LucideIcon; label: string; hint: string }[] = [
  { key: "upload", action: "upload", Icon: Upload, label: "Upload", hint: "A folder, a .zip, or one SKILL.md" },
  { key: "write", action: "write", Icon: PenLine, label: "Write", hint: "Start from a template in the editor" },
  { key: "ask", action: "askDefinable", Icon: WandSparkles, label: "Ask Definable", hint: "Describe it, and Definable drafts it" },
  { key: "github", action: "github", Icon: Github, label: "From GitHub", hint: "Copy one from a public repo" },
];

export default function NewSkillMenu({ onPick, canAsk = true, align = "end" }: {
  onPick: (way: NewSkillWay) => void;
  /** Off while the workspace has no Definable to ask. */
  canAsk?: boolean;
  align?: "start" | "center" | "end";
}) {
  const ways = WAYS.filter((w) => canAsk || w.key !== "ask");
  // Past Radix's close and focus restore, so a file picker or dialog opens cleanly
  const after = (way: NewSkillWay) => () => setTimeout(() => onPick(way), 0);
  const anyLive = ways.some((w) => isSkillActionLive(w.action));
  const trigger = (
    <BrandButton className="shrink-0 whitespace-nowrap disabled:opacity-40 disabled:pointer-events-none" disabled={!anyLive}>
      <Plus className="w-3.5 h-3.5" strokeWidth={2} />
      New skill
      <ChevronDown className="w-3 h-3 -mr-0.5 opacity-70" strokeWidth={2} />
    </BrandButton>
  );
  if (!anyLive) return <span title="Creating skills isn't available yet">{trigger}</span>;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-[272px] p-1 rounded-[12px] bg-[var(--surface)] border border-[var(--hairline-strong)] shadow-menu">
        {ways.map(({ key, action, Icon, label, hint }) => {
          const live = isSkillActionLive(action);
          return (
            <DropdownMenuItem
              key={key}
              disabled={!live}
              onSelect={after(key)}
              className="flex items-start gap-2.5 px-2 py-[7px] rounded-[8px] cursor-pointer focus:bg-[var(--accent-hover)]"
            >
              <Icon className="w-[14px] h-[14px] mt-[2px] shrink-0 text-[var(--text-secondary)]" strokeWidth={1.75} />
              <span className="min-w-0">
                <span className="block text-[13px] text-[var(--text-primary)]">{label}</span>
                <span className="block text-[12px] leading-[1.45] text-[var(--text-tertiary)]">{live ? hint : "Not available yet"}</span>
              </span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
